import { FC, useCallback, useEffect, useId, useRef, useState } from 'react';
import { isBrowser } from '@belong/env';
import { useLockBodyScroll } from '@belong/react-hooks/useLockBodyScroll';
import { ICON_NAMES, MODAL_TEST_IDS } from '@belong/types';
import { ButtonWithRef } from '../../actions/Button/ButtonBase';
import { Icon } from '../../Icon/Icon';
import ButtonGroup from '../../styles/ButtonGroup';
import { Heading } from '../../styles/Typography';
import CONTENT from './content.json';
import * as ModalStyles from './Modal.styles';
import { IModal } from './Modal.types';

export const BaseModal: FC<IModal> = ({
  onClose,
  isOpen,
  content,
  heading,
  ariaLabelClose = CONTENT.ariaClose,
  ariaDialogDescribedby,
  ButtonComponent = ButtonWithRef,
  isLoading,
  image,
  topIcon,
  primaryBtn,
  secondaryBtn,
  primaryLink,
  secondaryLink
}) => {
  // Guard rendering and imperative dialog calls until after client mount.
  const [hasMounted, setHasMounted] = useState(false);

  const headingId = `modal-heading-${useId()}`;
  const dialogRef = useRef<HTMLDialogElement>(null);

  const closeModal = useCallback(() => {
    onClose();
    dialogRef.current?.close();
  }, [onClose]);

  const handleEscape = useCallback(
    ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        closeModal();
      }
    },
    [closeModal]
  );

  // BEGIN :HAS() HACK

  const supportsHas = isBrowser() && CSS?.supports?.('selector(:has(.foo))');
  useLockBodyScroll(!supportsHas && isOpen);

  // END :HAS() HACK

  useEffect(() => {
    window.addEventListener('keydown', handleEscape);
    setHasMounted(true);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  // Keep the native dialog open state in sync with the controlled isOpen prop.
  useEffect(() => {
    if (!hasMounted) {
      return;
    }

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (isOpen && !dialog.open) {
      dialog.showModal();
      return;
    }

    if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [hasMounted, isOpen]);

  if (!hasMounted) {
    return null;
  }

  const hasButtonRow = Boolean(primaryBtn?.label ?? secondaryBtn?.label ?? primaryLink ?? secondaryLink);

  return (
    <ModalStyles.Dialog
      width={{ md: '5/6', lg: '2/3' }}
      ref={dialogRef}
      aria-labelledby={headingId}
      aria-describedby={ariaDialogDescribedby}
      aria-modal
      data-testid={MODAL_TEST_IDS.ROOT}
      isOpen={isOpen}
    >
      <ModalStyles.DialogBox data-testid={MODAL_TEST_IDS.DIALOG_BOX}>
        <ModalStyles.DialogHeader>
          {image && (
            <ModalStyles.Image src={[image.src, { w: 150, h: 150 }]} alt={image.alt} contentType={image.contentType} />
          )}
          {topIcon && <Icon {...topIcon} />}
          <Heading id={headingId} as="h1" variant="large" hasVisibleFocus tabIndex={-1}>
            {heading}
          </Heading>
        </ModalStyles.DialogHeader>
        <ModalStyles.DialogBody>{content}</ModalStyles.DialogBody>
        {hasButtonRow && (
          <ModalStyles.DialogFooter>
            <ButtonGroup hasControl={{ xs: ['column', 'right', false], lg: ['row', 'right', true] }}>
              {primaryBtn?.label && (
                <ButtonComponent
                  isLoading={isLoading}
                  type="button"
                  variant="primary"
                  width={{ xs: 'full', lg: 'default' }}
                  onClick={() => primaryBtn.onClick?.(onClose)}
                  data-testid={MODAL_TEST_IDS.DEFAULT_PRIMARY_BUTTON}
                >
                  {primaryBtn?.label}
                </ButtonComponent>
              )}
              {secondaryBtn?.label && (
                <ButtonComponent
                  type="button"
                  disabled={isLoading}
                  variant="secondary"
                  width={{ xs: 'full', lg: 'default' }}
                  onClick={() => secondaryBtn.onClick?.(onClose)}
                  data-testid={MODAL_TEST_IDS.DEFAULT_SECONDARY_BUTTON}
                >
                  {secondaryBtn.label}
                </ButtonComponent>
              )}
              {primaryLink && <ButtonComponent width={{ xs: 'full', sm: 'min' }} {...primaryLink} />}
              {secondaryLink && <ButtonComponent width={{ xs: 'full', sm: 'min' }} {...secondaryLink} />}
            </ButtonGroup>
          </ModalStyles.DialogFooter>
        )}

        <ModalStyles.CloseButton
          variant="primary"
          isSmall
          onClick={() => closeModal()}
          data-testid={MODAL_TEST_IDS.CLOSE_BUTTON}
          a11yLabel={ariaLabelClose}
        >
          <Icon aria-hidden name={ICON_NAMES.Close} size="small" />
        </ModalStyles.CloseButton>
      </ModalStyles.DialogBox>
    </ModalStyles.Dialog>
  );
};

BaseModal.displayName = 'BaseModal';
